import { Component, OnInit, OnDestroy, Input } from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from "@angular/forms";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { SupabaseService } from "../../services/supabase.service";
import { FeedbackService } from "../../services/feedback.service";
import { AuthService } from "../../services/auth.service";
import { Feedback } from "../../models/feedback.interface";
import { ModalComponent } from "../../shared/components/modal/modal.component";

@Component({
  selector: "app-feedback",
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ModalComponent],
  templateUrl: "./feedback.component.html",
  styleUrls: ["./feedback.component.scss"],
})
export class FeedbackComponent implements OnInit, OnDestroy {
  @Input() hideButton = false; // Hide the fixed feedback button
  isModalOpen = false;
  feedbackForm: FormGroup;
  subjects = ["Compliment", "Suggestion", "Bug", "Feature Request"];
  isSubmitting = false;
  submitSuccess = false;
  submitError = false;
  private destroy$ = new Subject<void>();
  
  // Toast notification
  showToast = false;
  toastMessage = '';

  constructor(
    private fb: FormBuilder,
    private supabase: SupabaseService,
    private feedbackService: FeedbackService,
    private authService: AuthService
  ) {
    this.feedbackForm = this.fb.group({
      subject: ["Suggestion", Validators.required],
      message: ["", Validators.required],
      wants_contact: [false],
      email: ["", []],
    });

    // Add email validator when wants_contact is true and pre-fill with user's email if logged in
    this.feedbackForm.get("wants_contact")?.valueChanges.subscribe((wants) => {
      const emailControl = this.feedbackForm.get("email");
      if (wants) {
        emailControl?.setValidators([Validators.required, Validators.email]);
        
        // Pre-fill with user's email if they are logged in
        const currentUser = this.authService.getCurrentUser();
        if (currentUser && currentUser.email && !emailControl?.value) {
          emailControl?.setValue(currentUser.email);
        }
      } else {
        emailControl?.clearValidators();
        emailControl?.setValue("");
      }
      emailControl?.updateValueAndValidity();
    });
  }

  ngOnInit() {
    this.isModalOpen = false;
    this.submitSuccess = false;
    this.submitError = false;
    this.feedbackService.openRequested$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.openModal());
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openModal() {
    this.isModalOpen = true;
    // Reset form and statuses
    this.feedbackForm.reset({ subject: "Suggestion", wants_contact: false });
    this.submitSuccess = false;
    this.submitError = false;
    this.showToast = false;
  }

  closeModal() {
    this.isModalOpen = false;
  }


  async submitFeedback() {
    if (this.feedbackForm.valid) {
      this.isSubmitting = true;
      this.submitError = false;

      try {
        const feedback: Feedback = {
          subject: this.feedbackForm.value.subject,
          message: this.feedbackForm.value.message,
          wants_contact: this.feedbackForm.value.wants_contact,
          email: this.feedbackForm.value.wants_contact
            ? this.feedbackForm.value.email
            : null,
        };

        const { error } = await this.supabase
          .getClient()
          .from("feedback")
          .insert([feedback]);

        if (error) throw error;

        // Show toast notification and close modal
        this.toastMessage = 'Thank you for your feedback!';
        this.showToast = true;
        setTimeout(() => this.showToast = false, 3000);
        this.closeModal();
      } catch (error) {
        console.error("Error submitting feedback:", error);
        this.submitError = true;
      } finally {
        this.isSubmitting = false;
      }
    }
  }
}
